import { useEffect, useState, type ComponentType } from 'react';

import { cn } from '@/lib/utils';

import type {
    AdminDailyChartProps,
    DailyCountPoint,
} from '@/components/admin/daily-chart-impl';

export type { AdminDailyChartProps, DailyCountPoint };

function ChartSkeleton({
    title,
    subtitle,
    summary,
    className,
    fullWidth = true,
    secondary,
}: Pick<
    AdminDailyChartProps,
    'title' | 'subtitle' | 'summary' | 'className' | 'fullWidth' | 'secondary'
>) {
    return (
        <section
            className={cn(
                'min-w-0 bg-black',
                fullWidth && 'col-span-full',
                className,
            )}
        >
            <div className="flex flex-wrap items-end justify-between gap-3 border-b border-white/10 px-3 py-4 sm:px-4 md:px-6">
                <div>
                    <h2 className="text-sm font-medium text-white">{title}</h2>
                    <p className="mt-0.5 text-[11px] text-white/35">
                        {subtitle}
                    </p>
                </div>
                <p className="text-[11px] text-white/40">
                    {summary ?? (
                        <span className="invisible" aria-hidden>
                            0 סה״כ · שיא 0
                        </span>
                    )}
                </p>
            </div>
            <div className="px-3 py-4 sm:px-4 sm:py-5 md:px-6" dir="ltr">
                <div
                    className={cn(
                        'w-full',
                        secondary ? 'h-48' : 'h-40',
                    )}
                    aria-hidden
                />
            </div>
        </section>
    );
}

/**
 * Client-only chart shell. Recharts must not load during SSR on Appwrite Sites.
 */
export function AdminDailyChart(props: AdminDailyChartProps) {
    const [Impl, setImpl] = useState<ComponentType<AdminDailyChartProps> | null>(
        null,
    );

    useEffect(() => {
        let cancelled = false;
        void import('@/components/admin/daily-chart-impl').then((mod) => {
            if (!cancelled) {
                setImpl(() => mod.AdminDailyChartImpl);
            }
        });
        return () => {
            cancelled = true;
        };
    }, []);

    if (!Impl) {
        return <ChartSkeleton {...props} />;
    }

    return <Impl {...props} />;
}
